import React, { useRef, useEffect, useState } from 'react';
import {
  PlayCircleOutlined,
  FilePdfOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import useReveal from '../../AIEmployees/useReveal';
import './SAShowcase.css';

/**
 * SAShowcase — the "one prompt, any genre" proof wall. A large featured video
 * player plus an interleaved strip of tiles (video → PDF → videos) that proves
 * breadth: a commercial, an equity report, a sci-fi short, an anime, an ad.
 *
 * Every artifact here was produced from a single text prompt. Videos lazy-load
 * (poster image first, src only when selected/featured) so the section stays
 * fast despite the media weight. Only the featured video autoplays muted.
 *
 * The PDF tile opens the real document; it is an illustrative, user-generated
 * single-prompt demo (as are the branded video concepts).
 */
const VIDEOS = [
  {
    id: 'trailblazer',
    kind: 'video',
    src: '/assets/super-agents/trailblazer.mp4',
    poster: '/assets/super-agents/posters/trailblazer.jpg',
    genre: 'Commercial',
    title: 'A finished automotive ad',
    blurb: 'City, mountain, and trail shots with on-screen text and a tagline, indistinguishable from a live shoot.',
  },
  {
    id: 'last_transmission',
    kind: 'video',
    src: '/assets/super-agents/last_transmission.mp4',
    poster: '/assets/super-agents/posters/last_transmission.jpg',
    genre: 'Cinematic sci-fi',
    title: 'The Last Transmission',
    blurb: 'An astronaut’s final log — hyper-real CGI, emotional voiceover, and a title card.',
  },
  {
    id: 'tang_sanzang',
    kind: 'video',
    src: '/assets/super-agents/tang_sanzang.mp4',
    poster: '/assets/super-agents/posters/tang_sanzang.jpg',
    genre: 'Watercolor anime',
    title: 'The Monk’s Journey',
    blurb: 'A hand-drawn anime short with lip-synced Chinese dialogue and subtitles, scored end to end.',
  },
  {
    id: 'coffee',
    kind: 'video',
    src: '/assets/super-agents/coffee-ad.mp4',
    poster: '/assets/super-agents/posters/coffee.jpg',
    genre: 'Brand ad',
    title: 'Mountain Peak Coffee',
    blurb: 'A 30-second brand spot — cinematics, on-screen text, music, and pacing.',
  },
];

// The PDF proof tile, interleaved after the first video.
const PDF_TILE = {
  id: 'oscr',
  kind: 'pdf',
  href: '/assets/super-agents/pdf/OSCR_Stock_Analysis_March_2026.pdf',
  poster: '/assets/super-agents/pdf/oscr_cover.jpg',
  genre: 'Equity research',
  title: 'A 10-page equity report',
  blurb: 'An institutional-grade HOLD note on Oscar Health — scenario model, peer comp, and risk tiers.',
};

// Interleave: first video, then the PDF, then the rest of the videos.
const TILES = [VIDEOS[0], PDF_TILE, ...VIDEOS.slice(1)];

/**
 * `compact` — used when the showcase is composed inside another hero (the
 * homepage hub): swaps the full section head (big H2 + sub) for a one-line
 * eyebrow bridge, so the video is visible immediately (mobile real estate).
 */
const SAShowcase = ({ compact = false }) => {
  const [ref, visible] = useReveal();
  const [active, setActive] = useState(VIDEOS[0]);
  // Muted only on first page load (browser autoplay policy requires it). Once
  // the visitor clicks a tile — a user gesture — we unmute and play with sound,
  // and every subsequent selection stays unmuted.
  const [muted, setMuted] = useState(true);
  const videoRef = useRef(null);

  // Keep the DOM muted property in sync (React's `muted` attribute alone is
  // unreliable across browsers) and drive play/pause from visibility + the
  // active selection.
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = muted;
    if (visible) v.play().catch(() => {});
    else v.pause();
  }, [visible, active, muted]);

  // Selecting a tile is a user gesture: unmute and feature it.
  const handleSelect = (tile) => {
    setMuted(false);
    setActive(tile);
  };

  return (
    <section className="sa-showcase aie-section" id="sa-showcase" ref={ref}>
      {compact ? (
        <div className="sa-showcase__mini-head">
          {/* A real H2 (not H1): the page keeps exactly one H1 — the hero's
              keyword headline. This keyword-bearing H2 is the SEO-correct
              weight for the proof-wall heading. */}
          <h2 className="sa-showcase__mini-title aie-display">
            Deep reasoning. Every modality.
          </h2>
          <p className="sa-showcase__mini-sub">
            An ad, an equity report, a sci-fi short, an anime. Every
            piece here was created from a single prompt.
          </p>
        </div>
      ) : (
        <div className="aie-section-head">
          <h2 className="aie-section-title aie-display">One prompt. Any genre.</h2>
          <p className="aie-section-sub">
            An ad, a 10-page equity report, a sci-fi short, an anime.
            Every piece below arrived finished from a single prompt.
          </p>
        </div>
      )}

      <div className={`aie-reveal ${visible ? 'is-visible' : ''}`}>
        <div className="sa-showcase__featured">
          <video
            ref={videoRef}
            key={active.id}
            className="sa-showcase__video"
            src={active.src}
            poster={active.poster}
            muted
            loop
            playsInline
            preload="metadata"
            controls
          />
        </div>

        <p className="sa-showcase__caption">
          <PlayCircleOutlined />{' '}
          <strong>{active.title}.</strong> {active.blurb}
        </p>

        <div className="sa-showcase__strip">
          {TILES.map((t) =>
            t.kind === 'pdf' ? (
              <a
                key={t.id}
                className="sa-showcase__tile sa-showcase__tile--pdf"
                href={t.href}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span className="sa-showcase__tile-thumb">
                  <img src={t.poster} alt={t.title} loading="lazy" />
                  <span className="sa-showcase__tile-kind"><FilePdfOutlined /> PDF</span>
                </span>
                <span className="sa-showcase__tile-meta">
                  <span className="sa-showcase__tile-genre">{t.genre}</span>
                  <span className="sa-showcase__tile-title">
                    {t.title} <ArrowRightOutlined />
                  </span>
                </span>
              </a>
            ) : (
              <button
                key={t.id}
                type="button"
                className={`sa-showcase__tile ${active.id === t.id ? 'is-active' : ''}`}
                onClick={() => handleSelect(t)}
              >
                <span className="sa-showcase__tile-thumb">
                  <img src={t.poster} alt={t.title} loading="lazy" />
                  <span className="sa-showcase__tile-kind"><PlayCircleOutlined /> {t.genre}</span>
                </span>
                <span className="sa-showcase__tile-meta">
                  <span className="sa-showcase__tile-genre">{t.genre}</span>
                  <span className="sa-showcase__tile-title">{t.title}</span>
                </span>
              </button>
            )
          )}
        </div>

        <p className="sa-showcase__fineprint">
          Illustrative examples generated by CellCog users from a single prompt.
          Brand names and logos are the property of their respective owners and
          do not imply any affiliation or endorsement.
        </p>
      </div>
    </section>
  );
};

export default SAShowcase;
